import { FunctionComponent, useEffect } from 'react'
import { Box, Button, FormControl, FormHelperText, TextField } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  useCreateOrganizationMutation,
  useOrganizationQuery,
} from 'services/organizations'
import { Loading } from 'components/Loading'
import { createOrganizationFormSchema } from './validations/organization-form-validation'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'
import { IOrganization } from 'types/organization'
import { IUser } from 'types/users'

interface Props {
  currentUser?: IUser
}

export const OrganizationForm: FunctionComponent<Props> = ({ currentUser }) => {
  const { t } = useTranslation()
  const schema = createOrganizationFormSchema(t)

  const args = {
    id: currentUser?.id as string,
  } as const

  const { data: organization, isLoading } = useOrganizationQuery(args, {
    skip: !currentUser?.id,
    refetchOnMountOrArgChange: true,
  })
  const [createOrganization, { isSuccess }] = useCreateOrganizationMutation()

  const {
    handleSubmit,
    reset,
    formState: { errors },
    register,
  } = useForm<IOrganization>({
    defaultValues: {
      name: organization?.name,
      executiveOfficerName: organization?.executiveOfficerName,
      taxId: organization?.taxId,
      address: organization?.address,
      email: organization?.email,
      phoneNumber: organization?.phoneNumber,
      description: organization?.description,
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (organization) {
      reset(organization)
    }
  }, [organization, reset])

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }
  }, [isSuccess, t])

  const onSubmit = (values: IOrganization) => {
    createOrganization({ ...organization, ...values })
  }

  if (isLoading) return <Loading fullPage={false} />

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="organization-form">
      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('organization-name')}</>}
          variant="outlined"
          error={!!errors.name}
          {...register('name')}
          disabled={currentUser?.id !== organization?.rootUserId}
        />
      </FormControl>

      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('director-name')}</>}
          variant="outlined"
          error={!!errors.executiveOfficerName}
          {...register('executiveOfficerName')}
          disabled={currentUser?.id !== organization?.rootUserId}
        />
      </FormControl>

      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('TIN')}</>}
          variant="outlined"
          error={!!errors.taxId}
          {...register('taxId')}
          disabled={currentUser?.id !== organization?.rootUserId}
        />
      </FormControl>

      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('address')}</>}
          variant="outlined"
          error={!!errors.address}
          {...register('address', { required: t('required') })}
        />

        {errors.address && (
          <FormHelperText error>{errors.address?.message}</FormHelperText>
        )}
      </FormControl>

      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('email-address')}</>}
          variant="outlined"
          error={!!errors.email}
          {...register('email', { required: t('required') })}
        />

        {errors.email && (
          <FormHelperText error>{errors.email?.message}</FormHelperText>
        )}
      </FormControl>

      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('phone-number')}</>}
          variant="outlined"
          error={!!errors.phoneNumber}
          {...register('phoneNumber', { required: t('required') })}
        />

        {errors.phoneNumber && (
          <FormHelperText error>{errors.phoneNumber?.message}</FormHelperText>
        )}
      </FormControl>
      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('description')}</>}
          variant="outlined"
          error={!!errors.phoneNumber}
          InputProps={{
            rows: 2,
            multiline: true,
            inputComponent: 'textarea',
          }}
          {...register('description', { required: t('required') })}
        />
        {errors.description && (
          <FormHelperText error>{errors.description?.message}</FormHelperText>
        )}
      </FormControl>

      <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
        <Button
          type="submit"
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          color="primary"
          form="organization-form"
        >
          {t('save')}
        </Button>
      </Box>
    </form>
  )
}
